import { Suspense, lazy, useCallback, useEffect, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useLocale, useTranslations } from "next-intl";
import { CalendarClock, PlusIcon } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Link,
  usePathname,
  useRouter,
  useSearchParams,
} from "@/i18n/navigation";
import { EventDialog } from "@/components/calendar/event-dialog";
import { useEvents } from "@/hooks/use-events";
import { useEventDialog } from "@/hooks/use-event-dialog";
import { isEventId, useEventDetail } from "@/hooks/use-event-detail";
import { useAppointmentRequests } from "@/hooks/use-appointment-requests";
import { useCalendarPreferences } from "@/hooks/use-calendar-preferences";
import type {
  CalendarDateRange,
  CalendarSelection,
} from "@/components/calendar/calendar-view";
import type { ApiEvent } from "@/types/api-helpers";
import "@/components/calendar/calendar.css";

export const Route = createFileRoute("/_dashboard/calendar")({
  validateSearch: (search: Record<string, unknown>) => ({
    event:
      typeof search.event === "string" && isEventId(search.event)
        ? search.event
        : undefined,
  }),
  component: CalendarPage,
});

const CalendarView = lazy(() =>
  import("@/components/calendar/calendar-view").then((mod) => ({
    default: mod.CalendarView,
  })),
);

function CalendarContent() {
  const t = useTranslations("CalendarPage");
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const rawSearch = searchParams.toString();
  const rawSelectedEventId = searchParams.get("event") ?? undefined;
  const selectedEventId = isEventId(rawSelectedEventId)
    ? rawSelectedEventId
    : undefined;
  const {
    events,
    isLoading,
    isFetching,
    isError: listError,
    isTruncated,
    refetch: refetchEvents,
    setDateRange,
  } = useEvents();
  const initialEvent = events.find((item) => item.id === selectedEventId);
  const {
    event: selectedEvent,
    isFetching: isDetailFetching,
    isError: detailError,
    isNotFound,
    refetch: refetchDetail,
  } = useEventDetail(selectedEventId, initialEvent);
  const { appointments } = useAppointmentRequests({
    scope: "queue",
    suppressGlobalError: true,
  });
  const openedEventId = useRef<string | null>(null);
  const { showWeekends, setShowWeekends } = useCalendarPreferences();

  function clearSelectedEvent() {
    if (pathname === "/calendar") router.replace("/calendar");
  }

  const { openEvent, openNew, dialogProps } = useEventDialog({
    onClose: clearSelectedEvent,
  });
  const pendingCount = appointments.filter(
    (a) => a.status === "submitted"
  ).length;

  useEffect(() => {
    if (pathname !== "/calendar") return;
    const expected = selectedEventId
      ? `event=${encodeURIComponent(selectedEventId)}`
      : "";
    if (rawSearch !== expected) {
      router.replace(expected ? `/calendar?${expected}` : "/calendar");
    }
  }, [pathname, rawSearch, router, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) {
      openedEventId.current = null;
      return;
    }
    if (!selectedEvent || openedEventId.current === selectedEventId) return;
    openedEventId.current = selectedEventId;
    openEvent(selectedEvent);
  }, [openEvent, selectedEvent, selectedEventId]);

  const handleDateSelect = useCallback(
    (arg: CalendarSelection) => {
      router.replace("/calendar");
      openNew({
        defaultStart: arg.startStr,
        defaultEnd: arg.endStr,
        defaultAllDay: arg.allDay,
      });
    },
    [openNew, router]
  );

  const handleEventClick = useCallback(
    (event: ApiEvent) => {
      router.push(`/calendar?event=${event.id}`);
    },
    [router]
  );

  const handleDatesSet = useCallback(
    (arg: CalendarDateRange) => {
      setDateRange(arg.startStr, arg.endStr);
    },
    [setDateRange]
  );

  return (
    <>
      <PageHeader title={t("title")} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <h1 className="sr-only">{t("title")}</h1>

        {(detailError || isNotFound) && (
          <div
            role="alert"
            className="flex flex-col items-start gap-3 rounded-md border p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <p className="text-muted-foreground text-sm">
              {isNotFound ? t("eventNotFound") : t("eventLoadError")}
            </p>
            <div className="flex gap-2">
              {!isNotFound && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void refetchDetail()}
                  disabled={isDetailFetching}
                >
                  {isDetailFetching ? t("retrying") : t("retry")}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={clearSelectedEvent}>
                {t("close")}
              </Button>
            </div>
          </div>
        )}

        {isTruncated && (
          <div role="status" className="rounded-md border p-3 text-sm">
            {t("eventLimitWarning")}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link href="/appointments">
              <CalendarClock className="size-4" />
              {t("appointmentRequests")}
              {pendingCount > 0 && (
                <Badge variant="info" className="ml-1">
                  {pendingCount}
                </Badge>
              )}
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              router.replace("/calendar");
              openNew();
            }}
          >
            <PlusIcon className="size-4" />
            {t("newEvent")}
          </Button>
        </div>
        {listError ? (
          <div
            role="alert"
            className="flex min-h-80 flex-col items-center justify-center gap-3 rounded-md border p-6 text-center"
          >
            <p className="text-muted-foreground text-sm">
              {t("eventsLoadError")}
            </p>
            <Button
              variant="outline"
              onClick={() => void refetchEvents()}
              disabled={isFetching}
            >
              {isFetching ? t("retrying") : t("retry")}
            </Button>
          </div>
        ) : (
          <Suspense
            fallback={<Skeleton className="h-[600px] w-full rounded-lg" />}
          >
            {isLoading ? (
              <Skeleton className="h-[600px] w-full rounded-lg" />
            ) : (
              <CalendarView
                events={events}
                locale={locale}
                onDateSelect={handleDateSelect}
                onEventClick={handleEventClick}
                onDatesSet={handleDatesSet}
                showWeekends={showWeekends}
                onShowWeekendsChange={setShowWeekends}
              />
            )}
          </Suspense>
        )}
      </div>

      <EventDialog {...dialogProps} />
    </>
  );
}

function CalendarPage() {
  return (
    <Suspense
      fallback={
        <div className="flex flex-1 flex-col gap-4 p-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-[600px] w-full rounded-lg" />
        </div>
      }
    >
      <CalendarContent />
    </Suspense>
  );
}
